import { TouchEvent } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useAppDispatch } from "../../store/hooks";
import { EventsActions, EventType } from "../../store/events-slice";
import { leaveEventForm } from "../../store/events-actions";

import classes from "./EventItem.module.css";
import { t } from "../../i18n";

const EventItem = (
  props: EventType & { onToggleImportant?: (event: EventType) => void }
) => {
  const { eventToDelete, eventToEdit } = useSelector(
    (state: RootState) => state.events
  );
  const dispatch = useAppDispatch();

  const deleteItemHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    dispatch(EventsActions.setConfirmDelete(props));
  };

  let touchStartCoords = {
    x: -1,
    y: -1,
  };
  let touchEndCoords = {
    x: -1,
    y: -1,
  };
  let startTime = 0;
  let elapsedTime = 0;
  let maxAllowedTime = 1000;
  let direction = "undefined";
  let minDistanceXAxis = 30;
  let maxDistanceYAxis = 30;

  const touchStartHandler = (e: TouchEvent<HTMLLIElement>) => {
    const e2 = e.changedTouches[0];
    touchStartCoords = {
      x: e2.pageX,
      y: e2.pageY,
    };
    startTime = new Date().getTime();
  };

  const touchMoveHandler = (e: TouchEvent<HTMLLIElement>) => {};

  const touchEndHandler = (e: TouchEvent<HTMLLIElement>) => {
    const e2 = e.changedTouches[0];
    touchEndCoords = {
      x: e2.pageX - touchStartCoords.x,
      y: e2.pageY - touchStartCoords.y,
    };
    elapsedTime = new Date().getTime() - startTime;
    if (elapsedTime <= maxAllowedTime) {
      if (
        Math.abs(touchEndCoords.x) >= minDistanceXAxis &&
        Math.abs(touchEndCoords.y) <= maxDistanceYAxis
      ) {
        direction = touchEndCoords.x < 0 ? "left" : "right";
        switch (direction) {
          case "left":
            dispatch(
              EventsActions.setDeleteEvent({
                item: props.id,
              })
            );
            break;
          case "right":
            dispatch(EventsActions.unsetDeleteEvent());
            break;
        }
      }
    }
  };

  let eventClass = `${classes.item} ${
    !props.timeFrom && !props.timeTill
      ? props.important
        ? classes.important
        : classes.regular
      : ""
  }`;
  const eventTime =
    props.timeFrom || props.timeTill ? (
      <span className={classes.time}>
        {props.timeFrom}
        {props.timeFrom && props.timeTill && <br />}
        {props.timeTill}
      </span>
    ) : (
      ""
    );
  // Any row without a time can be starred from the list, as in the mobile app:
  // a grey outline when off, filled blue when on. A row with a time shows the
  // time in this cell instead, so it is starred from the form.
  const canStar = !props.timeFrom && !props.timeTill;
  const eventImportant = canStar ? (
    <span className={classes.star}>
      <button
        type="button"
        className={`${classes.starButton} ${props.important ? classes.starOn : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          props.onToggleImportant?.(props);
        }}
        title={t.important}
        aria-label={t.important}
        aria-pressed={!!props.important}
      >
        <svg className="svg" viewBox="0 0 50 50">
          {/* The star's outer shape, filled in when Important is on. */}
          <path
            className={classes.starFill}
            d="M25 38.62 9.55 50l6.01-18.82L0 19.1l19.3.19L25 0l5.7 19.29 19.3-.19-15.56 12.08 6.01 18.82Z"
          />
          <path
            d="M7040,9511.62L7024.55,9523l6.01-18.82L7015,9492.1l19.3,0.19L7040,9473l5.7,19.29,19.3-.19-15.56,12.08,6.01,18.82Zm-14.41,8.13,13.22-9.74,1.19-.87,1.19,0.87,13.22,9.74,0.84,2.63-0.32.24-2.23-1.64-5.17-16.19-0.42-1.33,1.1-.86,13.49-10.47,2.63-.02,0.14,0.4-2.08,1.61-16.67.17-1.51.02-0.42-1.45-4.83-16.33,0.87-2.96h0.34l0.87,2.96-4.83,16.33-0.42,1.45-1.51-.02-16.67-.17-2.08-1.61,0.14-.4,2.63,0.02,13.49,10.47,1.1,0.86-0.42,1.33-5.17,16.19-2.23,1.64-0.32-.24Z"
            transform="translate(-7015 -9473)"
          />
        </svg>
      </button>
    </span>
  ) : (
    ""
  );
  if (eventToDelete === props.id) {
    eventClass += ` ${classes.swiped}`;
  }
  if (eventToEdit !== null && eventToEdit.id === props.id) {
    eventClass += ` ${classes.open}`;
  }

  // The row's action says what it will do: a tick finishes the event, while a
  // repeating one moves to its next date and a birthday to next year.
  const isBirthday = !!props.isBirthday;
  const isRepeating = isBirthday || (!!props.repeat && props.repeat !== "never");
  const actionLabel = isBirthday
    ? t.complete.birthdayConfirm
    : isRepeating
    ? t.complete.repeatConfirm
    : t.complete.confirm;

  const linkClass = `${classes.link} ${
    window.desktop ? classes.linkDesktop : ""
  }`;

  const viewItemHandler = () => {
    if (eventToEdit !== null && eventToEdit.id === props.id) {
      dispatch(leaveEventForm(EventsActions.unsetEditEvent()));
    } else {
      dispatch(leaveEventForm(EventsActions.setEditEvent(props)));
    }
  };

  return (
    <li
      className={eventClass}
      onClick={viewItemHandler}
      onTouchStart={touchStartHandler}
      onTouchMove={touchMoveHandler}
      onTouchEnd={touchEndHandler}
    >
      <div className={linkClass}>
        {eventTime}
        {eventImportant}
        <span className={classes.title}>{props.title}</span>
        <span className={classes.delete}>
          <button
            className={`${classes.deleteBtn} ${isRepeating ? classes.deleteBtnSquare : ""}`}
            onClick={deleteItemHandler}
            title={actionLabel}
            aria-label={actionLabel}
          >
            {isBirthday ? (
              // Framed tighter than the arrow: the cake's drawing leaves more
              // empty space, and in the same box it read a size smaller.
              <svg className="svg" viewBox="3 1.5 18 20">
                <path d="M4 21h16M5 21v-7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7M5 16.5c1.75 1.5 3.25 1.5 5 0 1.5 1.5 2.5 1.5 4 0 1.75 1.5 3.25 1.5 5 0M12 12V8.5M12 5.8c-.8-.9-.8-1.7 0-2.8.8 1.1.8 1.9 0 2.8Z" />
              </svg>
            ) : isRepeating ? (
              <svg className="svg" viewBox="0 0 24 24">
                <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
              </svg>
            ) : (
              <svg className="svg" viewBox="0 0 26 20">
                <path d="M1.5 10.5 9 18 24.5 2.5" />
              </svg>
            )}
          </button>
          <button className={classes.backBtn}>
            <svg className="svg" viewBox="0 0 26 42">
              <path
                className="events-list__item-back-path"
                d="M6854.88,9999l-21.88,21,21.88,21,4.12-4-17.75-17,17.75-17Z"
                transform="translate(-6833 -9999)"
              />
            </svg>
          </button>
        </span>
      </div>
    </li>
  );
};

export default EventItem;
